import { type FC, useContext, useState } from "react";
import PercentageInput from "../../../PercentageInput";
import { DragDropComponentErrorContext } from "../../DragDropComponent";
import {
    DragDropEditorContext,
    type DragDropSettingsProps,
    type InternalColumnComponentType,
    type InternalColumnType,
    isInternalColumnType,
} from "../../DragDropTypes";

function isInternalColumnValue(value: unknown): value is [ InternalColumnType, InternalColumnType ] {
    if(!Array.isArray(value)) {
        return false;
    }

    if(value.length !== 2) {
        return false;
    }

    return isInternalColumnType(value[0]) &&
        isInternalColumnType(value[1]);
}

export const ColumnComponentSettings: FC<DragDropSettingsProps> = ({
    id,
    value: valueProp,
}) => {
    const { manager } = useContext(DragDropEditorContext);

    const error = useContext(DragDropComponentErrorContext);

    const [ widths, setWidths ] = useState(() => {
        if(!isInternalColumnValue(valueProp)) {
            return [ "50", "50" ];
        }

        return [
            Math.round(valueProp[0].width * 100).toString(),
            Math.round(valueProp[1].width * 100).toString(),
        ];
    });

    const onChange = (index: number, value: string): void => {
        if(!isInternalColumnValue(valueProp)) {
            return;
        }

        const parsed = parseFloat(value);

        if(isNaN(parsed)) {
            setWidths(prevState => [
                ...prevState.slice(0, index),
                value,
                ...prevState.slice(index + 1),
            ]);

            return;
        }

        const remaining = Math.round(100 - parsed);

        let first: [ number, string ];
        let second: [ number, string ];

        if(index === 0) {
            first = [ parsed / 100, value ];
            second = [ remaining / 100, remaining.toString() ];
        } else {
            first = [ remaining / 100, remaining.toString() ];
            second = [ parsed / 100, value ];
        }

        setWidths([
            first[1],
            second[1],
        ]);

        manager.updateComponent<InternalColumnComponentType>(id, [{
            id   : valueProp[0].id,
            width: first[0],
        }, {
            id   : valueProp[1].id,
            width: second[0],
        }]);
    };

    if(!isInternalColumnValue(valueProp)) {
        return error;
    }

    return (
        <div>
            <label>
                Column Widths
            </label>
            <div className="grid grid-cols-2 gap-1">
                {widths.map((width, index) => (
                    <PercentageInput
                        key={index}
                        value={width}
                        onChange={(e) => {
                            onChange(index, e.currentTarget.value);
                        }}
                    />
                ))}
            </div>
        </div>
    );
};
